import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../../shared/api/client";
import type {
  PageResponse,
  ProjectModuleView,
  ProjectSummary,
  ReleaseDetail,
  ReleaseType,
} from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Select } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";
import { RELEASE_TYPE_LABELS } from "./releaseVocabulary";

const TYPES: ReleaseType[] = [
  "MAJOR", "MINOR", "PATCH", "HOTFIX", "EMERGENCY",
  "UPGRADE", "INFRASTRUCTURE", "CONFIGURATION",
];

const COPYABLE = [
  ["copyPrerequisites", "Pre-requisites"],
  ["copyApprovals", "Approvals"],
  ["copyExecutionSteps", "Runbook"],
  ["copyRollback", "Rollback plan"],
  ["copyValidation", "Post-deployment checks"],
] as const;

/**
 * Starts a release from another one's governance (§55).
 *
 * <p>What is copied is the process — the checks, the sign-offs, the runbook — and never the
 * scope. The whole reason 2.2 exists is that it carries different work from 2.1, so bringing the
 * tickets across would be wrong every time, and a checkbox offering it would be a trap.
 */
export function CloneReleaseDrawer({
  release,
  open,
  onClose,
}: {
  release: ReleaseDetail;
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [releaseName, setReleaseName] = useState("");
  const [version, setVersion] = useState("");
  const [releaseType, setReleaseType] = useState<ReleaseType>(release.releaseType);
  /* Where it is going. A shared component released in its own project is rolled out to a
     customer rather than released again, so the target project is a choice. */
  const [targetProjectId, setTargetProjectId] = useState(release.projectId);
  const [moduleIds, setModuleIds] = useState<string[]>([]);
  const [copy, setCopy] = useState<Record<string, boolean>>({
    copyPrerequisites: true,
    copyApprovals: true,
    copyExecutionSteps: true,
    copyRollback: true,
    copyValidation: true,
  });
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  useEffect(() => {
    if (!open) {
      return;
    }
    setReleaseName("");
    setVersion("");
    setReleaseType(release.releaseType);
    reset();
    setTargetProjectId(release.projectId);
    setModuleIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const projects = useQuery({
    queryKey: ["clone-projects"],
    queryFn: () => api.get<PageResponse<ProjectSummary>>("/api/v1/projects?size=200"),
    enabled: open,
  });

  /* The target project's shared components. Only shared ones: a module built for one project
     cannot be rolled out to another. */
  const targetModules = useQuery({
    queryKey: ["project-modules", targetProjectId],
    queryFn: () =>
      api.get<ProjectModuleView[]>(`/api/v1/projects/${targetProjectId}/modules`),
    enabled: open,
  });
  const shared = (targetModules.data ?? []).filter(
    (row) => row.module.moduleType.sharedVersion,
  );

  const canSubmit = releaseName.trim() !== "" && version.trim() !== "";

  const submit = () =>
    void run(async () => {
      const created = await api.post<ReleaseDetail>(
        `/api/v1/releases/${release.id}/clone`,
        {
          projectId: targetProjectId,
          releaseScope: moduleIds.length > 0 ? "COMPONENT" : "PROJECT",
          moduleIds,
          releaseName: releaseName.trim(),
          version: version.trim(),
          releaseType,
          ...copy,
        },
      );
      onClose();
      navigate(`/releases/${created.id}`);
    });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Start from this release"
      subtitle={`Copies ${release.releaseName}'s governance into a new release. The scope stays behind.`}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} loading={saving}>
            {saving ? "Creating…" : "Create"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onClose} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        {/* Rolling a shared component out to a customer: released once in its own project,
            taken by each project that uses it. The plan crosses; the people do not. */}
        <Field
          label="Into which project"
          hint="Another project takes this release's plan — the preparation, runbook and checks — and fills in its own people."
        >
          <Select
            value={targetProjectId}
            onChange={(event) => {
              setTargetProjectId(event.target.value);
              setModuleIds([]);
            }}
          >
            {(projects.data?.content ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
                {project.id === release.projectId ? " (this one)" : ""}
              </option>
            ))}
          </Select>
        </Field>

        {shared.length > 0 && (
          <Field
            label="As a rollout of"
            hint="Leave all unticked and this is an ordinary release of the whole product."
          >
            <div className="divide-y divide-ink-100 rounded-lg border border-ink-200">
              {shared.map((row) => (
                <label
                  key={row.module.id}
                  className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-ink-50"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 shrink-0 rounded border-ink-300"
                    checked={moduleIds.includes(row.module.id)}
                    onChange={() =>
                      setModuleIds((now) =>
                        now.includes(row.module.id)
                          ? now.filter((id) => id !== row.module.id)
                          : [...now, row.module.id],
                      )
                    }
                  />
                  <span className="min-w-0 flex-1 truncate text-sm text-ink-900">
                    {row.module.name}
                  </span>
                  <span className="tnum shrink-0 font-mono text-xs text-ink-500">
                    {row.currentVersion ? `on ${row.currentVersion}` : "no version yet"}
                  </span>
                </label>
              ))}
            </div>
          </Field>
        )}

        {targetProjectId !== release.projectId && copy.copyApprovals && (
          <InlineAlert tone="info">
            The approval seats come across; whoever fills them is the receiving project's
            decision. Nobody arrives pre-approved.
          </InlineAlert>
        )}

        <Field label="Name" required>
          <Input
            autoFocus
            value={releaseName}
            onChange={(event) => setReleaseName(event.target.value)}
            placeholder={`${release.releaseName} follow-up`}
          />
        </Field>

        <Field label="Version" required hint="Whatever your team calls it — nothing is parsed.">
          <Input
            value={version}
            onChange={(event) => setVersion(event.target.value)}
            placeholder="2.2"
          />
        </Field>

        <Field label="Kind of release" required>
          <Select
            value={releaseType}
            onChange={(event) => setReleaseType(event.target.value as ReleaseType)}
          >
            {TYPES.map((value) => (
              <option key={value} value={value}>
                {RELEASE_TYPE_LABELS[value]}
              </option>
            ))}
          </Select>
        </Field>

        <div>
          <p className="mb-1.5 text-sm font-medium text-ink-800">What to bring across</p>
          <div className="space-y-1.5">
            {COPYABLE.map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-ink-300"
                  checked={copy[key] ?? false}
                  onChange={(event) =>
                    setCopy((current) => ({ ...current, [key]: event.target.checked }))
                  }
                />
                {label}
              </label>
            ))}
          </div>
          <p className="mt-2 text-xs text-ink-500">
            The scope is never copied. A new release carries different work — that is what makes
            it a new release.
          </p>
        </div>
      </div>
    </Drawer>
  );
}
